import { useRef } from 'react';
import { Radio, DatePicker, TimePicker, Button, Space, Typography, InputNumber, Select, Tooltip } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

const { Text } = Typography;

const timeFormat = 'h:mm A';

const emptyEntry = { date: '', startTime: '09:00 AM', endTime: '05:00 PM' };

const CampaignAvailabilitySettings = ({ availabilitySettings, onChange }) => {
  const mode = availabilitySettings?.mode || 'default';
  const lastSpecificRef = useRef(null);

  // Stash the latest specific_dates settings whenever we have them
  if (availabilitySettings?.mode === 'specific_dates') {
    lastSpecificRef.current = availabilitySettings;
  }

  const sharedDefaults = {
    scheduleSequence: 'random',
    hrDuration: 1,
    minDuration: 0,
    maxPerInterviewSlot: 1,
  };

  const handleModeChange = (e) => {
    const newMode = e.target.value;
    if (newMode === 'default') {
      onChange(null);
    } else if (newMode === 'specific_dates') {
      // Restore previous specific_dates settings if available
      if (lastSpecificRef.current) {
        onChange({ ...lastSpecificRef.current, mode: 'specific_dates' });
      } else {
        onChange({
          mode: 'specific_dates',
          ...sharedDefaults,
          specificDates: [emptyEntry],
        });
      }
    }
  };

  const updateField = (field, value) => {
    onChange({ ...availabilitySettings, [field]: value });
  };

  const updateSpecificDate = (index, field, value) => {
    const updated = [...availabilitySettings.specificDates];
    updated[index] = { ...updated[index], [field]: value };
    onChange({ ...availabilitySettings, specificDates: updated });
  };

  const addSpecificDate = () => {
    onChange({
      ...availabilitySettings,
      specificDates: [
        ...availabilitySettings.specificDates,
        { date: '', startTime: '09:00 AM', endTime: '05:00 PM' },
      ],
    });
  };

  const removeSpecificDate = (index) => {
    const updated = availabilitySettings.specificDates.filter((_, i) => i !== index);
    onChange({
      ...availabilitySettings,
      specificDates: updated.length
        ? updated
        : [{ date: '', startTime: '09:00 AM', endTime: '05:00 PM' }],
    });
  };

  return (
    <div style={{ marginTop: 12 }}>
      <Text style={{ fontSize: 13, color: '#546e7a', display: 'block', marginBottom: 8 }}>
        Interview Availability
      </Text>

      <Radio.Group
        value={mode}
        onChange={handleModeChange}
        optionType="button"
        buttonStyle="solid"
        size="small"
        options={[
          { label: <span style={{ fontWeight: 'normal' }}>Default</span>, value: 'default' },
          { label: <span style={{ fontWeight: 'normal' }}>Specific Dates</span>, value: 'specific_dates' },
        ]}
      />

      {mode === 'default' && (
        <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 8 }}>
          Uses your global calendar settings — available days, times, and a 15-day rolling window from today.
        </Text>
      )}

      {mode === 'specific_dates' && (
        <div style={{ marginTop: 12, padding: 12, background: '#fafafa', borderRadius: 6, border: '1px solid #f0f0f0' }}>
          <Space direction="vertical" size={8} style={{ width: '100%' }}>
            {availabilitySettings.specificDates.map((entry, index) => (
              <div
                key={index}
                style={{
                  display: 'flex',
                  gap: 8,
                  alignItems: 'center',
                  padding: '6px 0',
                  borderBottom: index < availabilitySettings.specificDates.length - 1 ? '1px solid #f0f0f0' : 'none',
                }}
              >
                <div style={{ flex: 1 }}>
                  {index === 0 && <Text style={{ fontSize: 12, color: '#8c8c8c', display: 'block', marginBottom: 4 }}>Date</Text>}
                  <DatePicker
                    size="small"
                    style={{ width: '100%' }}
                    disabledDate={(current) => current && current.isBefore(dayjs().startOf('day'))}
                    value={entry.date ? dayjs(entry.date) : null}
                    onChange={(date) => updateSpecificDate(index, 'date', date ? date.format('YYYY-MM-DD') : '')}
                  />
                </div>
                <div>
                  {index === 0 && <Text style={{ fontSize: 12, color: '#8c8c8c', display: 'block', marginBottom: 4 }}>Start</Text>}
                  <TimePicker
                    size="small"
                    use12Hours
                    format={timeFormat}
                    style={{ width: 110 }}
                    allowClear={false}
                    value={entry.startTime ? dayjs(entry.startTime, timeFormat) : null}
                    onChange={(t) => updateSpecificDate(index, 'startTime', t ? t.format(timeFormat) : '')}
                  />
                </div>
                <div>
                  {index === 0 && <Text style={{ fontSize: 12, color: '#8c8c8c', display: 'block', marginBottom: 4 }}>End</Text>}
                  <TimePicker
                    size="small"
                    use12Hours
                    format={timeFormat}
                    style={{ width: 110 }}
                    allowClear={false}
                    value={entry.endTime ? dayjs(entry.endTime, timeFormat) : null}
                    onChange={(t) => updateSpecificDate(index, 'endTime', t ? t.format(timeFormat) : '')}
                  />
                </div>
                <div style={{ paddingTop: index === 0 ? 20 : 0 }}>
                  <Button
                    size="small"
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => removeSpecificDate(index)}
                  />
                </div>
              </div>
            ))}
            <Button
              size="small"
              type="dashed"
              icon={<PlusOutlined />}
              onClick={addSpecificDate}
              style={{ width: '100%' }}
            >
              Add Date
            </Button>
          </Space>
        </div>
      )}

      {mode !== 'default' && (
        <div style={{ marginTop: 12, padding: 12, background: '#fafafa', borderRadius: 6, border: '1px solid #f0f0f0' }}>
          <Space direction="vertical" size={8} style={{ width: '100%' }}>
            <div style={{ display: 'flex', gap: 8 }}>
              <div style={{ flex: 1 }}>
                <Text style={{ fontSize: 12, color: '#8c8c8c', display: 'block', marginBottom: 4 }}>Interview Duration</Text>
                <div style={{ display: 'flex', gap: 4 }}>
                  <InputNumber
                    size="small"
                    controls={false}
                    min={0}
                    max={8}
                    value={availabilitySettings.hrDuration ?? 1}
                    onChange={(v) => {
                      const hr = v ?? 0;
                      onChange({
                        ...availabilitySettings,
                        hrDuration: hr,
                        // If hr drops to 0 and min is also 0, bump min to 5
                        ...(hr === 0 && !(availabilitySettings.minDuration > 0) ? { minDuration: 5 } : {}),
                      });
                    }}
                    style={{ width: 128 }}
                    addonAfter="hr"
                  />
                  <InputNumber
                    size="small"
                    controls={false}
                    min={(availabilitySettings.hrDuration ?? 1) === 0 ? 5 : 0}
                    max={59}
                    step={5}
                    value={availabilitySettings.minDuration ?? 0}
                    onChange={(v) => {
                      const min = v ?? 0;
                      onChange({
                        ...availabilitySettings,
                        minDuration: min,
                        // If min drops to 0 and hr is also 0, bump hr to 1
                        ...(min === 0 && !(availabilitySettings.hrDuration > 0) ? { hrDuration: 1 } : {}),
                      });
                    }}
                    style={{ width: 128 }}
                    addonAfter="min"
                  />
                </div>
              </div>
              <div style={{ flex: 1 }}>
                <Text style={{ fontSize: 12, color: '#8c8c8c', display: 'block', marginBottom: 4 }}>Candidates per Slot</Text>
                <InputNumber
                  size="small"
                  controls={false}
                  min={1}
                  max={20}
                  value={availabilitySettings.maxPerInterviewSlot ?? 1}
                  onChange={(v) => updateField('maxPerInterviewSlot', v ?? 1)}
                  style={{ width: '100%' }}
                />
              </div>
            </div>
            <div>
              <Text style={{ fontSize: 12, color: '#8c8c8c', display: 'block', marginBottom: 4 }}>Scheduling Type</Text>
              <Select
                size="small"
                value={availabilitySettings.scheduleSequence || 'random'}
                onChange={(v) => updateField('scheduleSequence', v)}
                style={{ width: '100%' }}
                options={[
                  { label: 'Random (3 random available slots)', value: 'random' },
                  { label: 'Next Available (3 earliest slots)', value: 'next-available' },
                ]}
              />
            </div>
          </Space>
        </div>
      )}
    </div>
  );
};

export default CampaignAvailabilitySettings;
